import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../utils/supabase';
import { SheetLabel, UtSheet } from './UtSheet';
import { BonusBarChart } from './BonusBarChart';
import { dropoutBars, recoveryGames, streakBonusPct } from './sheetMaths';

interface Props {
  open: boolean;
  onClose: () => void;
  playerId: string;
  gameId: string;
  matchNumber?: number | string;
  /** Short game date for the eyebrow, e.g. "Wed 8 Oct". */
  dateLabel?: string;
  currentStreak: number;
  shieldTokensAvailable: number;
  /** Selected (not a reserve): the freed place goes to the reserves. */
  isSelected?: boolean;
  onDone: () => void;
}

/**
 * Drop out after selection, optionally spending a shield token to protect the
 * streak. Same RPC as the classic DropoutConfirmModal (dropout_with_shield),
 * which protects the current streak value.
 */
export const UtDropoutModal: React.FC<Props> = ({
  open,
  onClose,
  playerId,
  gameId,
  matchNumber,
  dateLabel,
  currentStreak,
  shieldTokensAvailable,
  isSelected = true,
  onDone,
}) => {
  const hasShields = shieldTokensAvailable > 0;
  const [useShield, setUseShield] = useState(hasShields && currentStreak > 0);
  const [busy, setBusy] = useState(false);
  const streak = Math.max(0, currentStreak);
  const bonus = streakBonusPct(streak);
  const meet = recoveryGames(streak);
  const shielded = hasShields && useShield;

  useEffect(() => {
    if (open) setUseShield(shieldTokensAvailable > 0 && currentStreak > 0);
  }, [open, shieldTokensAvailable, currentStreak]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('dropout_with_shield', {
        p_player_id: playerId,
        p_game_id: gameId,
        p_use_shield: shielded,
        p_admin_id: null,
      });
      if (error) throw error;
      const result = data as { success: boolean; message: string };
      if (!result.success) {
        toast.error(result.message);
        return;
      }
      toast.success(result.message);
      onDone();
      onClose();
    } catch (e) {
      toast.error(`Failed to process dropout: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  const eyebrow = [matchNumber ? `Match #${matchNumber}` : null, dateLabel].filter(Boolean).join(' · ');

  return (
    <UtSheet
      open={open}
      onClose={onClose}
      busy={busy}
      eyebrow={eyebrow || undefined}
      eyebrowClassName="text-ut-bad"
      title="Drop out?"
      cancelLabel="Stay in"
      main={{
        label: busy ? 'WORKING…' : shielded ? 'DROP OUT WITH SHIELD' : 'DROP OUT',
        onClick: confirm,
        className: shielded ? 'bg-ut-sky text-[#06121f]' : 'bg-ut-bad text-white',
      }}
    >
      <div className="flex items-center gap-3.5 rounded-xl bg-ut-card p-3.5">
        <span className="font-ut-display text-5xl font-extrabold leading-none text-ut-acc">{streak}</span>
        <div className="flex flex-col gap-0.5">
          <b>game streak</b>
          <span className="text-[13px] text-ut-mut">{streak > 0 ? `Worth +${bonus}% XP every game` : 'No streak bonus yet'}</span>
        </div>
      </div>

      {hasShields ? (
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Shield choice">
          <RadioOption
            checked={shielded}
            onSelect={() => setUseShield(true)}
            title={`Use a shield · ${shieldTokensAvailable} left`}
            desc={
              streak > 0
                ? `Your ${streak}-game streak is protected and eases down when you return.`
                : "You don't have a streak to protect yet, so a shield gains you nothing."
            }
          />
          <RadioOption
            checked={!shielded}
            onSelect={() => setUseShield(false)}
            title="Drop out without a shield"
            desc="Your streak resets to 0 when the game is completed."
          />
        </div>
      ) : (
        <div className="rounded-xl border border-ut-line bg-ut-card px-3.5 py-3 text-[13px] leading-[1.45]">
          <b>No shield tokens.</b> <span className="text-ut-mut">Your streak resets to 0 when the game is completed. You earn a shield every 10 games played.</span>
        </div>
      )}

      {streak > 0 && (
        <div className="flex flex-col gap-2 rounded-xl bg-ut-card p-3.5">
          <SheetLabel>Your XP bonus over the next games back</SheetLabel>
          <BonusBarChart bars={dropoutBars(streak, shielded)} scaleTo={bonus} />
          <span className={clsx('text-[13px] leading-[1.45]', !shielded && 'text-ut-bad')}>
            {shielded
              ? `Back to your natural streak after ${meet} ${meet === 1 ? 'game' : 'games'}. Miss another game without a shield and the protection is lost.`
              : `You lose the +${bonus}% bonus and start again from game 1.`}
          </span>
        </div>
      )}

      {isSelected && (
        <span className="text-xs leading-[1.45] text-ut-mut">
          Your place is offered to the reserves in order. Each gets a share of the time left before kick-off to accept.
        </span>
      )}
    </UtSheet>
  );
};

const RadioOption: React.FC<{ checked: boolean; onSelect: () => void; title: string; desc: string }> = ({ checked, onSelect, title, desc }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onSelect}
    className={clsx(
      'flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition',
      checked ? 'border-ut-acc bg-ut-acc-soft' : 'border-ut-line bg-ut-card hover:bg-ut-card2',
    )}
  >
    <span
      className={clsx(
        'mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2',
        checked ? 'border-ut-acc' : 'border-ut-mut',
      )}
    >
      <span className={clsx('h-2 w-2 rounded-full', checked ? 'bg-ut-acc' : 'bg-transparent')} />
    </span>
    <span className="flex flex-col gap-[3px]">
      <b className="text-[15px]">{title}</b>
      <span className="text-[13px] leading-[1.4] text-ut-mut">{desc}</span>
    </span>
  </button>
);
